import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { get } from "lodash";
import { BrowserView, MobileView } from "react-device-detect";
import { useSelector } from "react-redux";

import { STATIC_KEYWORDS } from "../../../constants";
// import { SvgIconHandler } from "../../atoms";
import { DfpComponent } from "../../../components/ads/dfp-component";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";

import "./inThisStoryWidget.m.css";

const InThisStoryWidget = ({ data }) => {
  const rankedEntites = data?.tags || [];
  if (!rankedEntites.length) return null;

  const filteredEntity = rankedEntites.filter((item) => {
    const props = item.properties || {};
    return (
      props.next500ranks ||
      props.fortune500ranks ||
      props.emergingcompaniesranks ||
      props["40u40ranks"] ||
      props.mpwranks
    );
  });
  if (!filteredEntity.length) {
    return null;
  }
  const getParsedRanks = (rankData, label) => {
    try {
      const parsed = JSON.parse(rankData);
      return parsed.map((entry) => {
        const year = parseInt(Object.keys(entry)[0]);
        return { year, type: label };
      });
    } catch (e) {
      return [];
    }
  };

  const RANK_PRIORITY = {
    "Fortune 500 India": 4,
    "The Next 500": 3,
    "Most Powerful Women": 2,
    "40 Under 40": 1,
    "The Emerging Companies": 5,
  };

  const filteredAndFormatted = filteredEntity
    .map((item) => {
      const props = item.properties || {};
      const ranks = [];

      if (props.fortune500ranks) {
        ranks.push(...getParsedRanks(props.fortune500ranks, "Fortune 500 India"));
      }

      if (props.next500ranks) {
        ranks.push(...getParsedRanks(props.next500ranks, "The Next 500"));
      }

      if (props.mpwranks || props.mpwrankstemp) {
        ranks.push(...getParsedRanks(props.mpwranks, "Most Powerful Women"));
      }

      if (props["40u40ranks"]) {
        ranks.push(...getParsedRanks(props["40u40ranks"], "40 Under 40"));
      }

      if (props.emergingcompaniesranks) {
        ranks.push(...getParsedRanks(props.emergingcompaniesranks, "The Emerging Companies"));
      }

      if (ranks.length > 0) {
        const maxYear = Math.max(...ranks.map((r) => r.year));
        const topYearRanks = ranks.filter((r) => r.year === maxYear);
        const bestRank = topYearRanks.sort((a, b) => RANK_PRIORITY[b.type] - RANK_PRIORITY[a.type])[0];

        return {
          name: item.name,
          hrefType:
            bestRank.type === "Fortune 500 India" ||
            bestRank.type === "The Next 500" ||
            bestRank.type === "The Emerging Companies"
              ? "companies"
              : "people",
          slug: item.slug,
          rankType: bestRank.type,
          rankYear: bestRank.year,
        };
      }

      return null;
    })
    .filter(Boolean);

  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";

  const containerRef = useRef(null);
  const buttonLeftRef = useRef(null);
  const buttonRightRef = useRef(null);
  const shadowLeftRef = useRef(null);
  const shadowRightRef = useRef(null);

  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const config = get(qtState, ["config"], {});
  const widgetConfig = get(qtState, ["config", "ads-config", "widgets"], {});
  const { thisStoryFeatureWidget = false } = widgetConfig;
  const isPhotoStory = data?.["story-template"] === "photo";

  const [adComponent, setAdComponent] = useState();
  const pageType = qtState.pageType;

  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  const headerSlotConfig = get(qtState, ["config", "ads-config", "ad_slots", pageType], {});
  const desktopAdUnit = get(headerSlotConfig, ["desktop", "sponsor", "ad_unit"], "");
  const mobileAdUnit = get(headerSlotConfig, ["mobile", "sponsor", "ad_unit"], "");

  useEffect(() => {
    setAdComponent(
      <>
        {desktopAdUnit && (
          <BrowserView>
            <DfpComponent
              adStyleName="ad-slot-size-74x16"
              id={`${pageType}-sponsor-ad`}
              path={`/${networkId}/${desktopAdUnit}`}
              adSlotConfigName="74x16_ad"
              showText={false}
            />
          </BrowserView>
        )}
        {mobileAdUnit && (
          <MobileView>
            <DfpComponent
              adStyleName="ad-slot-size-74x14"
              id={`${pageType}-sponsor-ad-mobile`}
              path={`/${networkId}/${mobileAdUnit}`}
              adSlotConfigName="74x14_ad"
              showText={false}
            />
          </MobileView>
        )}
      </>
    );

    const container = containerRef.current;
    if (!container) return;

    const leftRef = isMobile ? shadowLeftRef : buttonLeftRef;
    const rightRef = isMobile ? shadowRightRef : buttonRightRef;

    if (container.scrollWidth <= container.clientWidth) {
      if (leftRef.current) {
        leftRef.current.style.opacity = "0";
        leftRef.current.style.pointerEvents = "none";
      }
      if (rightRef.current) {
        rightRef.current.style.opacity = "0";
        rightRef.current.style.pointerEvents = "none";
      }
      return;
    }

    if (leftRef.current) {
      leftRef.current.style.opacity = "0";
      leftRef.current.style.pointerEvents = "none";
    }

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;

      const isAtRight = scrollLeft + clientWidth >= scrollWidth - 8;
      const isAtLeft = scrollLeft <= 1;

      if (rightRef.current) {
        rightRef.current.style.opacity = isAtRight ? "0" : "1";
        rightRef.current.style.pointerEvents = isAtRight ? "none" : "all";
      }

      if (leftRef.current) {
        leftRef.current.style.opacity = isAtLeft ? "0" : "1";
        leftRef.current.style.pointerEvents = isAtLeft ? "none" : "all";
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isMobile]);

  const scrollLeft = () => {
    containerRef.current?.scrollBy({ left: -100, behavior: "smooth" });
  };

  const scrollRight = () => {
    containerRef.current?.scrollBy({ left: 100, behavior: "smooth" });
  };

  const handleEntityClick = (item) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, data);
      const templateName = getTemplateName(data?.["story-template"] || pageType);
      window.dataLayer.push({
        event: "dl_story_interactions",
        link_url: `/${item.hrefType}/${item.slug}`,
        event_action: "entity_feature_click",
        link_text: item?.name?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  return (
    <div
      styleName={`parent-container ${isPhotoStory ? "photo-story-widget" : ""} ${
        data?.subheadline ? "" : "no-subheadline"
      }`}
    >
      <div styleName="title">{STATIC_KEYWORDS.THIS_STORY_FEATURES}</div>
      {thisStoryFeatureWidget && (
        <div styleName={`sponsor ${isPhotoStory ? "photo-story-sponsor" : ""}`}>
          <div styleName="label-text">Powered by</div>
          <div styleName="logo">{adComponent}</div>
        </div>
      )}
      <div styleName="divider" />
      <div styleName="slider-container">
        <div styleName="slider-button left" onClick={scrollLeft} ref={buttonLeftRef}>
          {/* <SvgIconHandler type="arrow" width="24" height="24" viewBox="0 0 24 24" /> */}
        </div>
        <div styleName="slider-list" ref={containerRef}>
          <div styleName="shadow left-shadow" ref={shadowLeftRef} />
          {filteredAndFormatted.map((item, index) => (
            <div styleName="scroll-item" key={index}>
              <a href={`/${item.hrefType}/${item.slug}`} onClick={() => handleEntityClick(item)}>
                <span styleName="name">{item.name}</span>
              </a>
              <span styleName="value">
                &bull;
                <span styleName="margin-left">{" " + item.rankType}</span>
                <span styleName="margin-right">{" " + item.rankYear}</span>
              </span>
              {index !== filteredAndFormatted.length - 1 && <span styleName="value item-divider" />}
            </div>
          ))}
          <div styleName="shadow right-shadow" ref={shadowRightRef} />
        </div>
        <div styleName="slider-button right" onClick={scrollRight} ref={buttonRightRef}>
          {/* <SvgIconHandler type="arrow" width="24" height="24" viewBox="0 0 24 24" /> */}
        </div>
      </div>
      <div styleName="divider" />
    </div>
  );
};

InThisStoryWidget.propTypes = {
  data: PropTypes.object,
};

export default InThisStoryWidget;
